import { Spinner } from "@chakra-ui/react"
import { GetGranularityConfiguration, Granularity } from "@hydrologiq/common/types/reporting/Granularity"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { IANATimeZone, toTimestamp } from "@hydrologiq/common/helpers/date"
import { DateTime } from "timezonecomplete"
import Download from "@hydrologiq/shared/reporting/Download"
import { usageCsv } from "@hydrologiq/shared/reporting/usageCsv"
import { type RowType } from "@hydrologiq/shared/reporting/rows"
import { CustomerUsage } from "@hydrologiq/gql/graphql"
import { useState, useEffect } from "react"
import { type ReportingSource } from "./CustomerReportingUsage"
import { fetchCustomerReportingData } from "./customerReportingApi"

/**
 * Maps the InfluxDB-style queryFrequency values from the Granularity config
 * to the timescale API frequency parameter values.
 */
function mapGranularityToTimescaleFrequency(queryFrequency: string): string {
  switch (queryFrequency) {
    case "5min":
      return "5min"
    case "1H":
      return "hourly"
    case "1D":
      return "daily"
    case "1MS":
      return "monthly"
    case "1W-Sun":
      return "weekly"
    default:
      return "daily"
  }
}

/**
 * Transforms a timescale API response row into the CustomerUsage shape
 * expected by the existing reporting display components.
 */
function transformTimescaleRowToCustomerUsage(row: Record<string, unknown>): CustomerUsage {
  return {
    __typename: "CustomerUsage",
    start: (row.bucket_start as string) ?? "",
    end: (row.bucket_end as string) ?? "",
    averagePower: (row.generation_power_average as number) ?? null,
    peakPower: (row.generation_power_peak as number) ?? null,
    peakPowerTime: (row.generation_power_peak_time as string) ?? null,
    usedEnergy: (row.generation_energy as number) ?? null,
    totalEmissions: (row.generation_co2e as number) ?? null,
    emissionsFactor: (row.generation_co2e_per_kwh as number) ?? null,
    usedWeight: (row.hydrogen_weight_consumed as number) ?? null,
    averageWeight: (row.hydrogen_weight_consumed as number) ?? null,
    startWeight: (row.hydrogen_weight_start as number) ?? null,
    endWeight: (row.hydrogen_weight_end as number) ?? null,
  }
}

interface CustomerUsageDownloadProps {
  start: DateTime
  end: DateTime
  granularity: Granularity
  customer: string
  customerName: string
  callback?: () => void
  selectedFields?: (keyof RowType)[]
  assetIds: string[]
  installationIds: string[]
  /** The active reporting data source. Defaults to influxdb (existing behavior). */
  source?: ReportingSource
  /** The selected site ID, used when source is "timescale" to filter by site. */
  selectedSiteId?: string
}

function CustomerUsageDownload({
  start,
  end,
  customer,
  customerName,
  assetIds,
  installationIds,
  granularity,
  callback,
  selectedFields,
  source,
  selectedSiteId,
}: CustomerUsageDownloadProps) {
  const granularityConfig = GetGranularityConfiguration(granularity)

  // Timescale download path state
  const [timescaleLoading, setTimescaleLoading] = useState<boolean>(source === "timescale")
  const [timescaleData, setTimescaleData] = useState<CustomerUsage[] | null>(null)
  const [timescaleError, setTimescaleError] = useState<string | null>(null)

  // Fetch full date range from timescale API for download
  useEffect(() => {
    if (source !== "timescale") return

    let cancelled = false
    setTimescaleLoading(true)
    setTimescaleError(null)

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("bearer_token") ?? ""
        const frequency = mapGranularityToTimescaleFrequency(granularityConfig.queryFrequency)
        const tz = IANATimeZone()

        const fields = [
          "generation_energy",
          "generation_power_average",
          "generation_power_peak",
          "generation_power_peak_time",
          "generation_co2e",
          "generation_co2e_per_kwh",
          "load_energy",
          "load_power_average",
          "load_power_peak",
          "load_power_peak_time",
          "hydrogen_weight_consumed",
          "hydrogen_weight_start",
          "hydrogen_weight_end",
          "diesel_fuel_consumed",
          "diesel_level_start",
          "diesel_level_end",
          "battery_soc_average",
          "battery_soc_peak",
          "battery_soc_min",
          "battery_discharge_energy",
          "battery_discharge_power_average",
          "battery_charge_energy",
          "battery_charge_power_average",
        ]

        const response = await fetchCustomerReportingData(
          customer,
          toTimestamp(start, true),
          toTimestamp(end, true),
          frequency,
          fields,
          token,
          tz,
          selectedSiteId,
        )

        if (!cancelled) {
          const transformed = response.rows.map(transformTimescaleRowToCustomerUsage)
          setTimescaleData(transformed)
        }
      } catch (err) {
        if (!cancelled) {
          setTimescaleData(null)
          if (err instanceof Error) {
            setTimescaleError(err.message)
          } else {
            setTimescaleError("Failed to load reporting data for download.")
          }
        }
      } finally {
        if (!cancelled) {
          setTimescaleLoading(false)
        }
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [source, customer, start, end, granularityConfig, selectedSiteId])

  // --- Timescale path ---
  if (source === "timescale") {
    if (timescaleLoading) return <Spinner />
    if (timescaleError || !timescaleData) return "An error has occurred: " + (timescaleError ?? "No data")

    return (
      <Download
        start={start}
        end={end}
        granularity={granularity}
        baseName={customerName}
        callback={callback}
        data={usageCsv(timescaleData, selectedFields)}
      />
    )
  }

  // --- InfluxDB path (existing behavior) ---
  return (
    <InfluxDownload
      start={start}
      end={end}
      customer={customer}
      customerName={customerName}
      assetIds={assetIds}
      installationIds={installationIds}
      granularity={granularity}
      granularityConfig={granularityConfig}
      callback={callback}
      selectedFields={selectedFields}
    />
  )
}

/** Existing GraphQL-based download logic extracted into a sub-component */
function InfluxDownload({
  start,
  end,
  customer,
  customerName,
  assetIds,
  installationIds,
  granularity,
  granularityConfig,
  callback,
  selectedFields,
}: {
  start: DateTime
  end: DateTime
  customer: string
  customerName: string
  assetIds: string[]
  installationIds: string[]
  granularity: Granularity
  granularityConfig: ReturnType<typeof GetGranularityConfiguration>
  callback?: () => void
  selectedFields?: (keyof RowType)[]
}) {
  const { loading, error, data } = useQueryWithEndpoint<gqlTypes.GetCustomerUsageQuery, gqlTypes.GetCustomerUsageQueryVariables>(
    gqlTypes.GetCustomerUsageDocument,
    {
      variables: {
        id: customer,
        start: toTimestamp(start, true),
        end: toTimestamp(end, true),
        freq: granularityConfig.queryFrequency,
        location: IANATimeZone(),
        assetIds: assetIds.join(','),
        installationIds: installationIds.join(','),
        kinds: undefined
      },
    },
    "primary"
  )

  if (loading) return <Spinner />
  if (error || !data) return "An error has occurred: " + error?.message

  return (
    <Download
      start={start}
      end={end}
      granularity={granularity}
      baseName={customerName}
      callback={callback}
      data={usageCsv(data.getCustomerUsage?.data as CustomerUsage[] || [], selectedFields)}
    />
  )
}

export default CustomerUsageDownload
